import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  TableFooter,
  Button,
} from "@mui/material";
import { ProductListProps } from "../..";
import {
  Delete,
  Pause,
  PlayArrow,
  OpenInNew,
  Check,
  Close,
} from "@mui/icons-material";

import {
  pauseProduct,
  unPauseProduct,
  deleteProduct,
} from "../../services/products.services";
import { approveProduct, deniedProduct } from "../../services/admin.services";
import { memo, useContext } from "react";
import TableDataPagination from "../TableDataPagination";
import NoResults from "../../../../components/Common/NoResults";
import TablaProducto from "./TablaProducto";
import AccionesMasivasButtons from "../AccionesMasivasButtons";
import Loading from "../../../../components/Common/Loading";
import useAccionesMasivas from "../../hooks/acciones.masivas.hook";
import { ProductosVendedorContext } from "../../context/productos.vendedor.context";
import { IRubro } from "../../../AdminProducts/types/producto";
import { ITenant } from "../../../AbmLocalPage/types";

const TABLE_HEADS = [
  "",
  "Imagen",
  "Código/SKU",
  "Nombre",
  "Rubro",
  "Precio",
  "Estado",
  "Estadisticas",
  "Acciones",
];

export const BUTTONS: any = {
  open: {
    text: "Detalle",
    icon: <OpenInNew />,
  },
  approve: {
    text: "Aprobar",
    icon: <Check />,
    action: (id: number, tenant: ITenant) => approveProduct(id, tenant),
  },
  denied: {
    text: "Rechazar",
    icon: <Close />,
    action: (id: number, tenant: ITenant) => deniedProduct(id, tenant),
  },
  pause: {
    text: "Pausar",
    icon: <Pause />,
    action: (id: number, tenant: ITenant) => pauseProduct(id, tenant),
  },
  resume: {
    text: "Reanudar",
    icon: <PlayArrow />,
    action: (id: number, tenant: ITenant) => unPauseProduct(id, tenant),
  },
  delete: {
    text: "Borrar",
    icon: <Delete />,
    action: (id: number, tenant: ITenant) => deleteProduct(id, tenant),
  },
};

interface TablaProps extends ProductListProps {
  loading: boolean;
  rowsPerPage: number;
  page: number;
  paginator: any;
  isMobile: boolean;
  usaSku: boolean;
}

interface TablaAccionesMasivasProps {
  isAdmin: boolean;
  rubros: IRubro[];
  handleDeleteProducts: () => void;
  handlePauseProducts: () => void;
  handleUnPauseProducts: () => void;
  handleChangeRubro: (idRubro: number, tenant: ITenant) => void;
  handleLeft: () => void;
  puedePausarVarios: boolean;
  puedeReanudarVarios: boolean;
  productosSeleccionados: number[];
}

export const TablaAccionesMasivas = ({
  isAdmin,
  rubros,
  handleDeleteProducts,
  handlePauseProducts,
  handleUnPauseProducts,
  handleChangeRubro,
  handleLeft,
  productosSeleccionados,
  puedePausarVarios,
  puedeReanudarVarios,
}: TablaAccionesMasivasProps) => {
  return (
    <Box
      sx={{
        padding: "20px",
        width: "100%",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        background: "#d1d1d1",
      }}
    >
      <Typography variant="h6">
        {productosSeleccionados.length}{" "}
        {productosSeleccionados.length === 1
          ? "producto seleccionado"
          : "productos seleccionados"}
      </Typography>
      <AccionesMasivasButtons
        isAdmin={isAdmin}
        rubros={rubros}
        handleDeleteProducts={handleDeleteProducts}
        handlePauseProducts={handlePauseProducts}
        handleUnPauseProducts={handleUnPauseProducts}
        handleChangeRubro={handleChangeRubro}
        handleLeft={handleLeft}
        puedePausarVarios={puedePausarVarios}
        puedeReanudarVarios={puedeReanudarVarios}
      />
    </Box>
  );
};

const Tabla = ({
  page,
  paginator,
  rowsPerPage,
  products,
  total,
  handleDeleteProduct,
  handleEditProduct,
  handlePauseProduct,
  handleResumeProduct,
  handleDeleteProducts,
  handlePauseProducts,
  handleUnPauseProducts,
  handleChangeRubro,
  isAdmin,
  loading,
  usaSku,
  rubros,
}: TablaProps) => {
  const {
    accionesMasivas,
    productosSeleccionados,
    puedePausarVarios,
    puedeReanudarVarios,
    handleLeft,
  } = useContext(ProductosVendedorContext);
  if (loading) return <Loading />;
  return (
    <TableContainer component={Paper}>
      {accionesMasivas && rubros && (
        <TablaAccionesMasivas
          isAdmin={isAdmin}
          rubros={rubros}
          handleDeleteProducts={handleDeleteProducts}
          handlePauseProducts={handlePauseProducts}
          handleUnPauseProducts={handleUnPauseProducts}
          handleChangeRubro={handleChangeRubro}
          handleLeft={handleLeft}
          puedePausarVarios={puedePausarVarios}
          puedeReanudarVarios={puedeReanudarVarios}
          productosSeleccionados={productosSeleccionados}
        />
      )}
      <Table sx={{ minWidth: 700 }}>
        <TableHead>
          <TableRow>
            {TABLE_HEADS.map((head, i) => {
              if (head === "Código/SKU" && !usaSku) {
                return null;
              }

              return (
                <TableCell key={i} sx={{ fontSize: "17px" }} align="center">
                  {head}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {products.length === 0 && (
            <TableRow>
              <TableCell colSpan={TABLE_HEADS.length}>
                <NoResults />
              </TableCell>
            </TableRow>
          )}

          {products.map((product: any, i: any) => (
            <TablaProducto
              key={i}
              product={product}
              isAdmin={isAdmin}
              handleResumeProduct={handleResumeProduct}
              handlePauseProduct={handlePauseProduct}
              handleDeleteProduct={handleDeleteProduct}
              handleEditProduct={handleEditProduct}
              handleDeleteProducts={handleDeleteProducts}
              handlePauseProducts={handlePauseProducts}
              handleUnPauseProducts={handleUnPauseProducts}
              handleChangeRubro={handleChangeRubro}
              loading={loading}
              usaSku={usaSku}
            />
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableDataPagination
              total={total || 0}
              page={page}
              paginator={paginator}
              productos={products}
              rowsPerPage={rowsPerPage}
              component={"td"}
            />
          </TableRow>
        </TableFooter>
      </Table>
      {accionesMasivas && rubros && (
        <TablaAccionesMasivas
          isAdmin={isAdmin}
          rubros={rubros}
          handleDeleteProducts={handleDeleteProducts}
          handlePauseProducts={handlePauseProducts}
          handleUnPauseProducts={handleUnPauseProducts}
          handleChangeRubro={handleChangeRubro}
          handleLeft={handleLeft}
          puedePausarVarios={puedePausarVarios}
          puedeReanudarVarios={puedeReanudarVarios}
          productosSeleccionados={productosSeleccionados}
        />
      )}
    </TableContainer>
  );
};

export default memo(Tabla);
